import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import {
  Lock, Mail, Eye, EyeOff, AlertCircle,
  Loader2, UserPlus, LogIn, CheckCircle,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import IvisoraLogo from '../../components/shared/IvisoraLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── CSS keyframes injected once ──────────────────────────────────────────── */
const STYLES = `
  @keyframes float-bg {
    0%, 100% { transform: translateY(0px) rotate(var(--r, 0deg)); }
    50%       { transform: translateY(-22px) rotate(var(--r, 0deg)); }
  }
  @keyframes spin-slow {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  @keyframes card-in {
    from { opacity: 0; transform: translateY(28px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes logo-in {
    from { opacity: 0; transform: translateY(-16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes shake-btn {
    0%,100% { transform: translateX(var(--dx,0px)); }
    20%     { transform: translateX(calc(var(--dx,0px) - 7px)); }
    40%     { transform: translateX(calc(var(--dx,0px) + 7px)); }
    60%     { transform: translateX(calc(var(--dx,0px) - 4px)); }
    80%     { transform: translateX(calc(var(--dx,0px) + 4px)); }
  }
  @keyframes success-pop {
    0%   { transform: scale(0.4) translateX(var(--dx,0px)); opacity: 0; }
    60%  { transform: scale(1.15) translateX(var(--dx,0px)); opacity: 1; }
    100% { transform: scale(1) translateX(var(--dx,0px)); opacity: 1; }
  }
  @keyframes error-in {
    from { opacity: 0; transform: translateY(-10px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes glow-red {
    0%,100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.5); }
    50%     { box-shadow: 0 0 0 8px rgba(239,68,68,0); }
  }
  @keyframes lock-shake {
    0%,100% { transform: rotate(0deg); }
    25%     { transform: rotate(-15deg); }
    75%     { transform: rotate(15deg); }
  }
  @keyframes input-glow {
    from { box-shadow: 0 0 0 0 rgba(139,92,246,0.6); }
    to   { box-shadow: 0 0 0 4px rgba(139,92,246,0); }
  }
`;

/* ─── Floating background shapes ───────────────────────────────────────────── */
const BG = [
  { id:1,  t:'circle', s:70,  l:'7%',   tp:'12%', c:'139,92,246',  op:.12, dur:8,  del:0,   r:0   },
  { id:2,  t:'circle', s:35,  l:'88%',  tp:'18%', c:'217,70,239',  op:.10, dur:6,  del:1.2, r:0   },
  { id:3,  t:'circle', s:90,  l:'4%',   tp:'65%', c:'109,40,217',  op:.09, dur:11, del:2,   r:0   },
  { id:4,  t:'circle', s:45,  l:'93%',  tp:'72%', c:'139,92,246',  op:.11, dur:7,  del:.5,  r:0   },
  { id:5,  t:'rect',   s:50,  l:'14%',  tp:'82%', c:'217,70,239',  op:.08, dur:9,  del:1.5, r:25  },
  { id:6,  t:'rect',   s:38,  l:'79%',  tp:'48%', c:'139,92,246',  op:.10, dur:12, del:3,   r:15  },
  { id:7,  t:'rect',   s:60,  l:'72%',  tp:'88%', c:'124,58,237',  op:.08, dur:14, del:.8,  r:-20 },
  { id:8,  t:'diamond',s:44,  l:'52%',  tp:'4%',  c:'217,70,239',  op:.09, dur:8,  del:2,   r:45  },
  { id:9,  t:'diamond',s:52,  l:'24%',  tp:'38%', c:'139,92,246',  op:.07, dur:13, del:1,   r:30  },
  { id:10, t:'line',   w:130, h:5,  l:'58%',  tp:'28%', c:'217,70,239', op:.12, dur:15, del:.5,  r:-30 },
  { id:11, t:'line',   w:90,  h:4,  l:'9%',   tp:'48%', c:'139,92,246', op:.10, dur:10, del:2.5, r:20  },
  { id:12, t:'line',   w:160, h:3,  l:'40%',  tp:'92%', c:'109,40,217', op:.08, dur:18, del:1,   r:5   },
  { id:13, t:'ring',   s:80,  l:'85%',  tp:'32%', c:'139,92,246',  op:.07, dur:16, del:0,   r:0   },
  { id:14, t:'ring',   s:50,  l:'35%',  tp:'15%', c:'217,70,239',  op:.06, dur:20, del:1,   r:0   },
  { id:15, t:'dots',            l:'68%',  tp:'10%', c:'217,70,239',  op:.15, dur:7,  del:1      },
  { id:16, t:'dots',            l:'20%',  tp:'72%', c:'139,92,246',  op:.12, dur:9,  del:2.5    },
];

const FloatingBg = ({ parallax }) => (
  <div
    className="absolute inset-0 overflow-hidden pointer-events-none select-none"
    style={{
      transform: `translate(${parallax.x * 0.4}px, ${parallax.y * 0.3}px)`,
      transition: 'transform 0.08s linear',
    }}
  >
    {BG.map(el => {
      const anim = `float-bg ${el.dur}s ease-in-out ${el.del}s infinite`;
      const base = {
        position: 'absolute',
        left: el.l,
        top: el.tp,
        animation: anim,
        '--r': `${el.r || 0}deg`,
        opacity: el.op,
      };
      if (el.t === 'circle') return (
        <div key={el.id} style={{ ...base, width: el.s, height: el.s, borderRadius: '50%', background: `rgb(${el.c})` }} />
      );
      if (el.t === 'rect') return (
        <div key={el.id} style={{ ...base, width: el.s, height: el.s, borderRadius: 8, background: `rgb(${el.c})`, transform: `rotate(${el.r}deg)` }} />
      );
      if (el.t === 'diamond') return (
        <div key={el.id} style={{ ...base, width: el.s, height: el.s, background: `rgb(${el.c})`, transform: `rotate(${el.r}deg)`, borderRadius: 4 }} />
      );
      if (el.t === 'ring') return (
        <div key={el.id} style={{ ...base, width: el.s, height: el.s, borderRadius: '50%', border: `3px solid rgb(${el.c})`, background: 'transparent' }} />
      );
      if (el.t === 'line') return (
        <div key={el.id} style={{ ...base, width: el.w, height: el.h, borderRadius: el.h, background: `rgb(${el.c})`, transform: `rotate(${el.r}deg)` }} />
      );
      if (el.t === 'dots') return (
        <div key={el.id} style={{ ...base, display: 'flex', gap: 6 }}>
          {[0,1,2,3,4].map(i => (
            <div key={i} style={{
              width: i === 2 ? 11 : 8,
              height: i === 2 ? 11 : 8,
              borderRadius: '50%',
              background: `rgb(${el.c})`,
              opacity: i % 2 === 0 ? 1 : 0.6,
            }} />
          ))}
        </div>
      );
      return null;
    })}
  </div>
);

/* ─── Dodge positions (translateX values) ───────────────────────────────────── */
const DODGE_XS = [-72, 72, -48, 48, -60];
let _lastDodge = 2;
const nextDodge = () => {
  const candidates = DODGE_XS.map((x, i) => ({ x, i })).filter(({ i }) => i !== _lastDodge);
  const pick = candidates[Math.floor(Math.random() * candidates.length)];
  _lastDodge = pick.i;
  return pick.x;
};

/* ─── Component ─────────────────────────────────────────────────────────────── */
const DesignerLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [tab, setTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginState, setLoginState] = useState('idle'); // idle | loading | success
  const [error, setError] = useState('');
  const [errorCount, setErrorCount] = useState(0);
  const [btnAnim, setBtnAnim] = useState(''); // '' | 'shake'
  const [btnDx, setBtnDx] = useState(0);     // dodge/nudge offset px
  const [lockShake, setLockShake] = useState(false);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const dodgeResetRef = useRef(null);

  useEffect(() => {
    if (localStorage.getItem('user_token')) navigate('/designer/dashboard');
  }, [navigate]);

  /* ── Parallax ── */
  const handleParallax = useCallback((e) => {
    const { width, height, left, top } = e.currentTarget.getBoundingClientRect();
    setParallax({
      x: ((e.clientX - left - width / 2) / width) * 28,
      y: ((e.clientY - top - height / 2) / height) * 18,
    });
  }, []);

  /* ── Button dodge ── */
  const handleBtnMouseEnter = useCallback(() => {
    if (loginState !== 'idle' || errorCount < 3) return;
    clearTimeout(dodgeResetRef.current);
    setBtnDx(nextDodge());
  }, [loginState, errorCount]);

  const handleBtnMouseLeave = useCallback(() => {
    if (errorCount < 3) return;
    dodgeResetRef.current = setTimeout(() => setBtnDx(0), 1800);
  }, [errorCount]);

  /* ── Error triggers ── */
  const triggerErrorEffects = useCallback((count) => {
    // Lock shake
    setLockShake(true);
    setTimeout(() => setLockShake(false), 600);
    // Shake on 1st error
    if (count === 1) {
      setBtnAnim('shake');
      setTimeout(() => setBtnAnim(''), 600);
    }
    // Nudge on 2nd error (permanent until reset)
    if (count === 2) {
      setBtnDx(-10);
    }
    // 3rd+: dodge mode (handled via mouseenter)
  }, []);

  /* ── Login ── */
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!API_URL) { setError('Backend not configured.'); return; }
    setLoading(true);
    setLoginState('loading');
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(
        typeof data.detail === 'string' ? data.detail :
        Array.isArray(data.detail) ? data.detail.map(d => d.msg || JSON.stringify(d)).join(', ') :
        'Invalid email or password.'
      );
      if (data.user.role !== 'designer') throw new Error('This account does not have designer access.');
      localStorage.setItem('user_token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setLoginState('success');
      setBtnDx(0);
      setBtnAnim('');
      toast({ title: 'Welcome back!', description: `Logged in as ${data.user.name}` });
      setTimeout(() => navigate('/designer/dashboard'), 1300);
    } catch (err) {
      const newCount = errorCount + 1;
      setErrorCount(newCount);
      setError(err?.message || 'Invalid email or password.');
      setLoginState('idle');
      triggerErrorEffects(newCount);
    } finally {
      setLoading(false);
    }
  };

  /* ── Register ── */
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Name is required'); return; }
    if (!API_URL) { setError('Backend not configured.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/designer/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(
        typeof data.detail === 'string' ? data.detail :
        Array.isArray(data.detail) ? data.detail.map(d => d.msg || JSON.stringify(d)).join(', ') :
        'Registration failed'
      );
      localStorage.setItem('user_token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      toast({ title: 'Account created!', description: 'Welcome to iVisora.' });
      navigate('/designer/dashboard');
    } catch (err) {
      setError(err?.message || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  /* ── Button style helper ── */
  const btnStyle = {
    transform: `translateX(${btnDx}px)`,
    transition: 'transform 0.25s cubic-bezier(0.34,1.56,0.64,1)',
    animation: btnAnim === 'shake' ? `shake-btn 0.55s ease-in-out` : undefined,
    background: loginState === 'success'
      ? 'linear-gradient(to right,#16a34a,#15803d)'
      : 'linear-gradient(to right,#7c3aed,#a21caf)',
  };

  return (
    <>
      <style>{STYLES}</style>
      <div
        className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0f0c29, #302b63, #24243e)' }}
        onMouseMove={handleParallax}
      >
        <FloatingBg parallax={parallax} />

        {/* Radial glow behind card */}
        <div
          className="absolute rounded-full pointer-events-none"
          style={{
            width: 600, height: 600,
            background: 'radial-gradient(circle, rgba(124,58,237,0.18) 0%, transparent 70%)',
            left: '50%', top: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />

        <div className="w-full max-w-md relative z-10">
          {/* Logo */}
          <div
            className="text-center mb-8"
            style={{ animation: 'logo-in 0.6s ease-out both' }}
          >
            <div className="inline-flex flex-col items-center gap-2">
              <IvisoraLogo variant="white" className="h-10 sm:h-11" />
              <p className="text-[11px] tracking-[0.18em] uppercase text-violet-300">Designer Portal</p>
            </div>
          </div>

          {/* Card */}
          <div style={{ animation: 'card-in 0.55s cubic-bezier(0.22,1,0.36,1) 0.1s both' }}>
            <Card
              className="border-0"
              style={{
                background: 'rgba(255,255,255,0.07)',
                backdropFilter: 'blur(24px)',
                WebkitBackdropFilter: 'blur(24px)',
                border: '1px solid rgba(255,255,255,0.12)',
                boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
              }}
            >
              {/* Tabs */}
              <div className="flex" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                {[
                  { key: 'login',    label: 'Sign In',       Icon: LogIn   },
                  { key: 'register', label: 'Create Account', Icon: UserPlus },
                ].map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    onClick={() => { setTab(key); setError(''); setErrorCount(0); setBtnDx(0); setBtnAnim(''); }}
                    className="flex-1 flex items-center justify-center gap-2 py-4 text-sm font-semibold transition-all duration-200"
                    style={{
                      color: tab === key ? '#fff' : 'rgba(196,181,253,0.5)',
                      borderBottom: tab === key ? '2px solid #a78bfa' : '2px solid transparent',
                    }}
                  >
                    <Icon className="w-4 h-4" /> {label}
                  </button>
                ))}
              </div>

              <CardContent className="pt-6 pb-8">
                {/* ── Login form ── */}
                {tab === 'login' ? (
                  <form onSubmit={handleLogin} className="space-y-4">
                    <CardHeader className="px-0 pt-0 pb-2">
                      <CardTitle className="text-xl text-white">Welcome back</CardTitle>
                      <CardDescription style={{ color: 'rgba(196,181,253,0.7)' }}>
                        Sign in to your designer account
                      </CardDescription>
                    </CardHeader>

                    {/* Error banner */}
                    {error && (
                      <div
                        className="px-4 py-3 rounded-xl flex items-center gap-3 text-sm"
                        style={{
                          animation: 'error-in 0.3s ease-out both, glow-red 1.2s ease-out',
                          background: 'rgba(239,68,68,0.15)',
                          border: '1px solid rgba(239,68,68,0.45)',
                          color: '#fca5a5',
                        }}
                      >
                        <Lock
                          className="w-4 h-4 flex-shrink-0"
                          style={lockShake ? { animation: 'lock-shake 0.4s ease-in-out' } : undefined}
                        />
                        {error}
                        {errorCount >= 2 && (
                          <span className="ml-auto text-xs opacity-60">
                            {errorCount >= 3 ? '😤 Good luck!' : '⚠️ Try again'}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Email */}
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium" style={{ color: 'rgba(196,181,253,0.9)' }}>
                        Email
                      </Label>
                      <div className="relative group">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors"
                          style={{ color: 'rgba(167,139,250,0.7)' }} />
                        <input
                          type="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          required
                          className="w-full h-11 pl-9 pr-4 rounded-xl text-sm text-white placeholder-violet-300/40 outline-none transition-all duration-200"
                          style={{
                            background: 'rgba(255,255,255,0.08)',
                            border: '1px solid rgba(139,92,246,0.3)',
                          }}
                          onFocus={e => {
                            e.target.style.border = '1px solid rgba(139,92,246,0.8)';
                            e.target.style.boxShadow = '0 0 0 3px rgba(139,92,246,0.2)';
                          }}
                          onBlur={e => {
                            e.target.style.border = '1px solid rgba(139,92,246,0.3)';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </div>
                    </div>

                    {/* Password */}
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium" style={{ color: 'rgba(196,181,253,0.9)' }}>
                        Password
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                          style={{ color: 'rgba(167,139,250,0.7)' }} />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          required
                          className="w-full h-11 pl-9 pr-10 rounded-xl text-sm text-white placeholder-violet-300/40 outline-none transition-all duration-200"
                          style={{
                            background: 'rgba(255,255,255,0.08)',
                            border: '1px solid rgba(139,92,246,0.3)',
                          }}
                          onFocus={e => {
                            e.target.style.border = '1px solid rgba(139,92,246,0.8)';
                            e.target.style.boxShadow = '0 0 0 3px rgba(139,92,246,0.2)';
                          }}
                          onBlur={e => {
                            e.target.style.border = '1px solid rgba(139,92,246,0.3)';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                        <button type="button" onClick={() => setShowPassword(v => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                          style={{ color: 'rgba(167,139,250,0.7)' }}
                          onMouseEnter={e => e.currentTarget.style.color = '#fff'}
                          onMouseLeave={e => e.currentTarget.style.color = 'rgba(167,139,250,0.7)'}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Hint for dodge mode */}
                    {errorCount >= 3 && loginState === 'idle' && (
                      <p className="text-xs text-center" style={{ color: 'rgba(196,181,253,0.5)', animation: 'error-in 0.3s ease-out' }}>
                        The button is feeling shy… try to catch it 😅
                      </p>
                    )}

                    {/* Submit button */}
                    <div className="overflow-visible pt-1">
                      <button
                        type="submit"
                        disabled={loading || loginState === 'success'}
                        onMouseEnter={handleBtnMouseEnter}
                        onMouseLeave={handleBtnMouseLeave}
                        className="w-full h-11 rounded-xl text-white font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 relative overflow-visible disabled:opacity-70"
                        style={btnStyle}
                        onMouseOver={e => {
                          if (loginState !== 'success' && errorCount < 3) {
                            e.currentTarget.style.opacity = '0.9';
                            e.currentTarget.style.boxShadow = '0 0 20px rgba(124,58,237,0.6)';
                          }
                        }}
                        onMouseOut={e => {
                          e.currentTarget.style.opacity = '';
                          e.currentTarget.style.boxShadow = '';
                        }}
                      >
                        {loginState === 'success' ? (
                          <span className="flex items-center gap-2" style={{ animation: 'success-pop 0.5s ease-out' }}>
                            <CheckCircle className="w-5 h-5" /> Signed in!
                          </span>
                        ) : loading ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</>
                        ) : (
                          'Sign In'
                        )}
                      </button>
                    </div>
                  </form>

                ) : (
                  /* ── Register form ── */
                  <form onSubmit={handleRegister} className="space-y-4">
                    <CardHeader className="px-0 pt-0 pb-2">
                      <CardTitle className="text-xl text-white">Create your account</CardTitle>
                      <CardDescription style={{ color: 'rgba(196,181,253,0.7)' }}>
                        Join iVisora as a template designer
                      </CardDescription>
                    </CardHeader>

                    {error && (
                      <div
                        className="px-4 py-3 rounded-xl flex items-center gap-2 text-sm"
                        style={{
                          animation: 'error-in 0.3s ease-out both',
                          background: 'rgba(239,68,68,0.15)',
                          border: '1px solid rgba(239,68,68,0.45)',
                          color: '#fca5a5',
                        }}
                      >
                        <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
                      </div>
                    )}

                    {[
                      { label: 'Full Name', type: 'text', value: name, set: setName, placeholder: 'Your name', icon: null },
                      { label: 'Email', type: 'email', value: email, set: setEmail, placeholder: 'you@example.com', icon: Mail },
                    ].map(({ label, type, value, set, placeholder, icon: Icon }) => (
                      <div key={label} className="space-y-1.5">
                        <Label className="text-sm font-medium" style={{ color: 'rgba(196,181,253,0.9)' }}>{label}</Label>
                        <div className="relative">
                          {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'rgba(167,139,250,0.7)' }} />}
                          <input
                            type={type}
                            placeholder={placeholder}
                            value={value}
                            onChange={e => set(e.target.value)}
                            required
                            className="w-full h-11 rounded-xl text-sm text-white placeholder-violet-300/40 outline-none transition-all duration-200"
                            style={{
                              paddingLeft: Icon ? '2.25rem' : '1rem',
                              paddingRight: '1rem',
                              background: 'rgba(255,255,255,0.08)',
                              border: '1px solid rgba(139,92,246,0.3)',
                            }}
                            onFocus={e => {
                              e.target.style.border = '1px solid rgba(139,92,246,0.8)';
                              e.target.style.boxShadow = '0 0 0 3px rgba(139,92,246,0.2)';
                            }}
                            onBlur={e => {
                              e.target.style.border = '1px solid rgba(139,92,246,0.3)';
                              e.target.style.boxShadow = 'none';
                            }}
                          />
                        </div>
                      </div>
                    ))}

                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium" style={{ color: 'rgba(196,181,253,0.9)' }}>Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'rgba(167,139,250,0.7)' }} />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Min. 8 characters"
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          required
                          className="w-full h-11 pl-9 pr-10 rounded-xl text-sm text-white placeholder-violet-300/40 outline-none transition-all duration-200"
                          style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(139,92,246,0.3)' }}
                          onFocus={e => {
                            e.target.style.border = '1px solid rgba(139,92,246,0.8)';
                            e.target.style.boxShadow = '0 0 0 3px rgba(139,92,246,0.2)';
                          }}
                          onBlur={e => {
                            e.target.style.border = '1px solid rgba(139,92,246,0.3)';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                        <button type="button" onClick={() => setShowPassword(v => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2"
                          style={{ color: 'rgba(167,139,250,0.7)' }}>
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full h-11 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 mt-2 disabled:opacity-70 transition-opacity"
                      style={{ background: 'linear-gradient(to right,#7c3aed,#a21caf)' }}
                      onMouseOver={e => { e.currentTarget.style.boxShadow = '0 0 20px rgba(124,58,237,0.5)'; }}
                      onMouseOut={e => { e.currentTarget.style.boxShadow = ''; }}
                    >
                      {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating account…</> : 'Create Account'}
                    </button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>

          <p className="text-center text-sm mt-6" style={{ color: 'rgba(167,139,250,0.5)' }}>
            Create an account to start submitting templates for review.
          </p>
        </div>
      </div>
    </>
  );
};

export default DesignerLogin;
